import React, {
  useEffect, useLayoutEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useMediaBreakpoint } from '@americanexpress/consumer-travel-base';
import { KEYCODE_TAB } from '../../constants/keyConstants';
import styles from './Popup.scss';
import { useWindowWidth } from '../../hooks/useWindowWidth';
import { isZoomIn } from '../../utils/eventUtils';
import { useDateSelector } from '../../context/dateContext';
import {
  geIsOutSideClickCloseEnabled,
} from '../../selectors/commonSelectors';
import { useIsHomePageVariantHook } from '../../hooks/useIsHomePageVariantHook';

export const bindClickOutside = ({ handler, ref }) => (event) => {
  if (ref?.current && !ref.current.contains(event.target)) {
    event.stopPropagation();
    handler(event);
  }
};

export const bindTabOutside = ({
  ref, handler,
}) => (event) => {
  // We close the popover on the next tick by using setTimeout.
  // This is necessary because we need to first check if
  // another child of the element has received focus as
  // the any event fires prior to the new focus event.
  setTimeout(() => {
    if (
      event.keyCode === KEYCODE_TAB
      && ref?.current
      && !ref.current.contains(document.activeElement)
    ) {
      event.stopPropagation();
      handler(event);
    }
  }, 0);
};

export const useOnClickOutside = (
  ref,
  handler,
  isOutSideClickCloseEnabled
) => {
  useEffect(() => {
    if (isOutSideClickCloseEnabled) {
      const onClickCheckIfFocusIsOutsidePopup = bindClickOutside({ handler, ref });

      document.addEventListener('mousedown', onClickCheckIfFocusIsOutsidePopup);
      document.addEventListener('touchstart', onClickCheckIfFocusIsOutsidePopup);

      return () => {
        document.removeEventListener('mousedown', onClickCheckIfFocusIsOutsidePopup);
        document.removeEventListener('touchstart', onClickCheckIfFocusIsOutsidePopup);
      };
    }
    return null;
  }, [ref, handler, isOutSideClickCloseEnabled]);
};

export const useOnTabOutside = ({
  ref,
  handler,
  isOutSideClickCloseEnabled,
}) => {
  useEffect(() => {
    if (isOutSideClickCloseEnabled) {
      const onKeyPressCheckIfFocusIsOutsidePopup = bindTabOutside({ ref, handler });
      document.addEventListener('keydown', onKeyPressCheckIfFocusIsOutsidePopup);
      return () => { document.removeEventListener('keydown', onKeyPressCheckIfFocusIsOutsidePopup); };
    }
    return null;
  }, [ref, handler, isOutSideClickCloseEnabled]);
};

export const calculateChildXPosition = ({
  windowWidth,
  popupWidth,
  triggerWidth,
  triggerRightPosition,
}) => {
  const centralXOffset = (popupWidth - triggerWidth) / 2;

  const isOverflowingLeftSideOfViewport = triggerRightPosition + centralXOffset < popupWidth;
  if (isOverflowingLeftSideOfViewport) {
    const overflowAmount = popupWidth - (triggerRightPosition + centralXOffset);
    return overflowAmount - centralXOffset;
  }

  const isOverflowingRightSideOfViewport = triggerRightPosition + centralXOffset > windowWidth - 20;
  if (isOverflowingRightSideOfViewport) {
    const predictedPopupRightPos = triggerRightPosition + (centralXOffset * 2);

    return windowWidth - predictedPopupRightPos - 20;
  }

  return centralXOffset * -1;
};

export const bindCalculateOffsetPosition = ({
  popupRef,
  startDateRef,
  endDateRef,
  isOpen,
  setPopupXOffset,
  windowWidth,
}) => () => {
  const popup = popupRef?.current;
  const startDate = startDateRef?.current;
  const endDate = endDateRef?.current;

  if (isOpen && popup && startDate) {
    const startDateRect = startDate.getBoundingClientRect();

    if (endDate) {
      const endDateRect = endDate.getBoundingClientRect();
      setPopupXOffset(calculateChildXPosition({
        windowWidth,
        popupWidth: popup.offsetWidth,
        triggerWidth: endDateRect.right - startDateRect.left,
        triggerRightPosition: endDateRect.right,
      }));
    } else {
      setPopupXOffset(calculateChildXPosition({
        windowWidth,
        popupWidth: popup.offsetWidth,
        triggerWidth: startDate.offsetWidth,
        triggerRightPosition: startDateRect.right,
      }));
    }
  }
};

const Popup = ({
  isOpen,
  onClose,
  children,
  startDateRef,
  endDateRef,
}) => {
  const [popupXOffset, setPopupXOffset] = useState(null);
  const popupRef = React.useRef();
  const isMobile = useMediaBreakpoint('sm-down');
  const windowWidth = useWindowWidth();
  const isOutSideClickCloseEnabled = useDateSelector(geIsOutSideClickCloseEnabled);

  useEffect(() => {
    onClose();
  }, [isMobile, onClose]);

  const adjustPosition = () => {
    setTimeout(() => {
      if (popupRef && popupRef.current && popupRef.current.style) {
        if (isZoomIn(400) || window.devicePixelRatio >= 4) {
          popupRef.current.style.position = 'absolute';
          popupRef.current.style.bottom = 'unset';
        } else if (isMobile) {
          popupRef.current.style.position = 'fixed';
          popupRef.current.style.bottom = 0;
        }
      }
    }, 0);
  };

  useEffect(() => {
    adjustPosition();
    window.addEventListener('resize', adjustPosition);
    return () => {
      window.removeEventListener('resize', adjustPosition);
    };
  }, [isOpen]);

  useOnClickOutside(
    popupRef,
    onClose,
    isOutSideClickCloseEnabled
  );

  useOnTabOutside({
    ref: popupRef,
    handler: onClose,
    isOutSideClickCloseEnabled,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- layout an arrow func as easier to test
  useLayoutEffect(bindCalculateOffsetPosition({
    popupRef,
    startDateRef,
    endDateRef,
    setPopupXOffset,
    windowWidth,
    isOpen,
  }), [isOpen, windowWidth, popupRef, startDateRef, endDateRef, setPopupXOffset]);

  const isXSViewport = useMediaBreakpoint('xs-down');
  const isVariantRedesign = useIsHomePageVariantHook();
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`${styles.popup} ${isVariantRedesign && isXSViewport && 'anim-slide-from-right'}`}
      style={{
        left: popupXOffset,
      }}
      ref={popupRef}
      role="tooltip"
      data-testid="date-picker-popup"
    >
      {children}
    </div>
  );
};

Popup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  startDateRef: PropTypes.node,
  endDateRef: PropTypes.node,
};

Popup.defaultProps = {
  startDateRef: null,
  endDateRef: null,
};

Popup.defaultProps = {
  isOpen: false,
};

export default Popup;
